import { Alert, AlertDescription, AlertTitle } from '@voll-intelligence/ui-kit/ui/alert';
import { Button } from '@voll-intelligence/ui-kit/ui/button';

import type { ChatUiHotelRateWarning } from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { fmtMoney } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { WarningIcon } from '@phosphor-icons/react';
import { hotelRateWarningReasons } from '../hotel_rate_warning_reasons.js';

/**
 * Aviso de mudança de tarifa na gramática dos recibos de reserva: mesma
 * moldura (borda, `rounded-md` do checkout, corpo em degraus de 14px com
 * padding 16/18/18), o hotel abrindo o card e o alerta como caixa própria.
 *
 * Sem o teto de 520px dos recibos: ocupa a coluna inteira que a shell entrega.
 * Aquela medida é largura de LEITURA de documento — quem confirma uma reserva
 * lê um comprovante. Isto interrompe uma decisão, não se lê como documento.
 *
 * O aviso vive num `Alert variant="soft" color="warning"` do DS — não numa
 * `<h3>` seguida de `<ul>`. Duas coisas vêm junto com o primitivo e nenhuma
 * existia aqui: o par soft/strong da cor (o card não tinha NENHUM sinal
 * cromático de que era um aviso) e o `role="alert"`, live region assertiva. É o
 * tratamento certo: a pessoa está a um clique de reservar uma tarifa cujas
 * condições mudaram debaixo dela.
 *
 * Um motivo só é frase; dois ou mais viram lista com marcador. Sem a distinção,
 * um único aviso ganhava um bullet solto — a lista de um item lê como fragmento.
 */
export function HotelRateWarningCard({
  warning,
  readOnly = false,
  dispatch,
}: {
  readonly warning: ChatUiHotelRateWarning;
  readonly readOnly?: boolean;
  readonly dispatch: GenUiDispatch;
}) {
  const { t, locale } = useGenUiI18n();
  const reasons = hotelRateWarningReasons(warning.warnings, locale);

  return (
    <section
      className="w-full overflow-hidden rounded-md border border-border bg-card shadow-(--shadow-elevation)"
      aria-label={t('hotelRateWarning.title')}
    >
      <div className="space-y-3.5 px-[18px] pt-4 pb-[18px]">
        {/* Zona de identidade do recibo de hotel: a hospedagem nomeia, a
          acomodação anota, o preço fecha a linha à direita — é o número que
          sustenta a decisão entre continuar e trocar, e estava rebaixado a
          texto muted no meio da frase do quarto. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-heading text-foreground">{warning.property}</h2>
            {warning.room && (
              <p className="text-detail mt-1 text-muted-foreground">{warning.room}</p>
            )}
          </div>
          {warning.price && (
            <div className="shrink-0 text-right">
              <p className="text-title text-foreground">{fmtMoney(warning.price)}</p>
              {/* O anterior riscado (<s> é semântico: "não vale mais") — só
                  quando o wire trouxe o par E o valor de fato mudou. */}
              {warning.previousPrice &&
                (warning.previousPrice.amount !== warning.price.amount ||
                  warning.previousPrice.currency !== warning.price.currency) && (
                  <p className="text-detail text-muted-foreground">
                    <s>{fmtMoney(warning.previousPrice)}</s>
                  </p>
                )}
            </div>
          )}
        </div>

        <Alert variant="soft" color="warning" className="text-ui px-3.5">
          <WarningIcon size={16} weight="fill" aria-hidden />
          <AlertTitle>{t('hotelRateWarning.title')}</AlertTitle>
          <AlertDescription className="text-detail">
            {reasons.length === 1 ? (
              <p>{reasons[0]}</p>
            ) : (
              <ul className="list-disc space-y-0.5 pl-4">
                {reasons.map((reason, index) => (
                  // Posição + código cru: o texto traduzido muda com o locale, e o
                  // código sozinho não serve porque o schema aceita repetido — dois
                  // `breakfast_changed` colidiriam na mesma chave. A lista é fixa
                  // (nunca reordena, nunca insere no meio), então a posição é
                  // identidade estável.
                  <li key={`${String(index)}:${warning.warnings[index] ?? ''}`}>{reason}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>

        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => {
                dispatch({
                  type: 'hotel-room-select',
                  offerId: warning.offerId,
                  roomIndex: warning.roomIndex,
                  ...(warning.price ? { price: warning.price } : {}),
                  text: t('hotelRateWarning.continueText'),
                });
              }}
            >
              {t('hotelRateWarning.continue')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                dispatch({
                  type: 'hotel-select',
                  offerId: warning.offerId,
                  text: t('hotelRateWarning.chooseAnotherText'),
                });
              }}
            >
              {t('hotelRateWarning.chooseAnother')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
